import type { ExerciseDef } from './types'

// ─── 9.1 Attention ────────────────────────────────────────────────────────────

export const attend: ExerciseDef = {
  starter: `// attend(query, keys, values): a soft dictionary lookup.
//   1. score each key:  s_i = query · key_i
//   2. weights = softmax(scores)
//   3. return Σ weights_i · values_i     (values are vectors here)
function attend(query: number[], keys: number[][], values: number[][]): number[] {
  return values[0]
}

const keys = [[1, 0], [0, 1], [-1, 0]]
const values = [[10, 0], [0, 10], [5, 5]]
console.log(attend([3, 0], keys, values))     // mostly the first value
console.log(attend([0, 0], keys, values))     // an even blend of all three
`,
  solution: `function attend(query: number[], keys: number[][], values: number[][]): number[] {
  const scores = keys.map(k => vec.dot(query, k))
  const w = vec.softmax(scores)
  let out = vec.zeros(values[0].length)
  values.forEach((v, i) => (out = vec.add(out, vec.scale(v, w[i]))))
  return out
}

const keys = [[1, 0], [0, 1], [-1, 0]]
const values = [[10, 0], [0, 10], [5, 5]]
console.log(attend([3, 0], keys, values))
console.log(attend([0, 0], keys, values))
`,
  tests: `
const __keys = [[1, 0], [0, 1], [-1, 0]]
const __values = [[10, 0], [0, 10], [5, 5]]
test("a zero query blends the values evenly", () => expect(attend([0, 0], __keys, __values), "attend([0,0], ...)").toBeCloseTo([5, 5]))
test("a strong query picks out its matching value", () => {
  const out = attend([20, 0], __keys, __values)
  expect(out, "attend([20,0], ...)").toBeCloseTo([10, 0], 1e-3)
})
test("matches the reference for a mixed query", () => expect(attend([2, 1], __keys, __values), "attend([2,1], ...)").toBeCloseTo([7.2801, 2.7199], 1e-3))
`,
}

// ─── 9.2 Self-attention ───────────────────────────────────────────────────────

export const selfAttention: ExerciseDef = {
  starter: `// Self-attention on a whole sequence, with plain arrays.
//   Q = X·Wq   K = X·Wk   V = X·Wv          (mat.mul)
//   scores = Q·Kᵀ / √d                       (d = number of columns of Q)
//   if causal: scores[i][j] = −Infinity for j > i
//   weights = softmax of each row            (vec.softmax)
//   output = weights · V
function selfAttention(X: number[][], Wq: number[][], Wk: number[][], Wv: number[][], causal: boolean): number[][] {
  return X
}

const X = [[1, 0], [0, 1], [1, 1]]
const I = [[1, 0], [0, 1]]
console.log(selfAttention(X, I, I, I, true))
`,
  solution: `function selfAttention(X: number[][], Wq: number[][], Wk: number[][], Wv: number[][], causal: boolean): number[][] {
  const Q = mat.mul(X, Wq)
  const K = mat.mul(X, Wk)
  const V = mat.mul(X, Wv)
  const d = Q[0].length
  const scores = mat.mul(Q, mat.transpose(K)).map((row, i) =>
    row.map((s, j) => (causal && j > i ? -Infinity : s / Math.sqrt(d))),
  )
  const weights = scores.map(row => vec.softmax(row))
  return mat.mul(weights, V)
}

const X = [[1, 0], [0, 1], [1, 1]]
const I = [[1, 0], [0, 1]]
console.log(selfAttention(X, I, I, I, true))
`,
  tests: `
const __X = [[1, 0], [0, 1], [1, 1]]
const __I = [[1, 0], [0, 1]]
test("the first token (causal) can only attend to itself", () => expect(selfAttention(__X, __I, __I, __I, true)[0], "row 0").toBeCloseTo([1, 0]))
test("causal output matches the reference", () => expect(selfAttention(__X, __I, __I, __I, true), "output").toBeCloseTo([[1, 0], [0.3302, 0.6698], [0.7517, 0.7517]], 1e-3))
test("non-causal lets the first token see everything", () => expect(selfAttention(__X, __I, __I, __I, false)[0], "row 0, non-causal").toBeCloseTo([0.8022, 0.5989], 1e-3))
test("output has one row per token and V's width", () => expect(mat.shape(selfAttention(__X, __I, __I, [[1, 2, 3], [4, 5, 6]], true)), "shape").toEqual([3, 3]))
`,
}

// ─── 9.3 The block ────────────────────────────────────────────────────────────

export const layerNorm: ExerciseDef = {
  starter: `// layerNorm(x, gamma, beta): normalise one token's vector.
//   mean = average of x;  variance = average of (x − mean)²
//   xhat_i = (x_i − mean) / √(variance + 1e−5)
//   return gamma_i · xhat_i + beta_i
function layerNorm(x: number[], gamma: number[], beta: number[]): number[] {
  return x
}

// residual(x, f): a residual connection — add the sub-layer's output back to its input.
function residual(x: number[], f: (v: number[]) => number[]): number[] {
  return f(x)
}

const ones = [1, 1, 1, 1], zeros = [0, 0, 0, 0]
console.log(layerNorm([2, 4, 6, 8], ones, zeros))
console.log(residual([1, 2], v => v.map(a => a * 10)))
`,
  solution: `function layerNorm(x: number[], gamma: number[], beta: number[]): number[] {
  const mean = vec.mean(x)
  const variance = vec.mean(x.map(v => (v - mean) ** 2))
  return x.map((v, i) => gamma[i] * ((v - mean) / Math.sqrt(variance + 1e-5)) + beta[i])
}

function residual(x: number[], f: (v: number[]) => number[]): number[] {
  return vec.add(x, f(x))
}

const ones = [1, 1, 1, 1], zeros = [0, 0, 0, 0]
console.log(layerNorm([2, 4, 6, 8], ones, zeros))
console.log(residual([1, 2], v => v.map(a => a * 10)))
`,
  tests: `
test("output has mean 0 (with γ = 1, β = 0)", () => expect(vec.mean(layerNorm([2, 4, 6, 8], [1, 1, 1, 1], [0, 0, 0, 0])), "mean").toBeCloseTo(0))
test("output has variance ≈ 1", () => {
  const y = layerNorm([2, 4, 6, 8], [1, 1, 1, 1], [0, 0, 0, 0])
  expect(vec.mean(y.map(v => v * v)), "variance").toBeCloseTo(1, 1e-4)
})
test("γ scales and β shifts", () => expect(layerNorm([1, 3], [2, 2], [5, 5]), "layerNorm([1,3], γ=2, β=5)").toBeCloseTo([3, 7], 1e-4))
test("residual adds the input back", () => expect(residual([1, 2], v => v.map(a => a * 10)), "residual").toEqual([11, 22]))
test("a residual around a zero layer is the identity", () => expect(residual([4, 5, 6], v => v.map(() => 0)), "residual(x, zero)").toEqual([4, 5, 6]))
`,
}

// ─── 9.4 Tiny GPT ─────────────────────────────────────────────────────────────

export const attentionHead: ExerciseDef = {
  starter: `// One causal self-attention head, with Tensors (so it can be trained).
// x: T × C (one row per token); wq, wk, wv: C × hs.
//   q = x·wq, k = x·wk, v = x·wv
//   att = (q · kᵀ) × (1/√hs) → maskCausal() → softmax()
//   return att · v                                    (T × hs)
function head(x: Tensor, wq: Tensor, wk: Tensor, wv: Tensor): Tensor {
  return x.matmul(wv)
}

const x = Tensor.randn(5, 8)
const out = head(x, Tensor.randn(8, 4), Tensor.randn(8, 4), Tensor.randn(8, 4))
console.log(out.shape, out.toString())
`,
  solution: `function head(x: Tensor, wq: Tensor, wk: Tensor, wv: Tensor): Tensor {
  const q = x.matmul(wq)
  const k = x.matmul(wk)
  const v = x.matmul(wv)
  const hs = wq.cols
  const att = q.matmul(k.transpose()).mul(1 / Math.sqrt(hs)).maskCausal().softmax()
  return att.matmul(v)
}

const x = Tensor.randn(5, 8)
const out = head(x, Tensor.randn(8, 4), Tensor.randn(8, 4), Tensor.randn(8, 4))
console.log(out.shape, out.toString())
`,
  tests: `
test("output shape is T × head size", () => expect(head(Tensor.randn(6, 8), Tensor.randn(8, 3), Tensor.randn(8, 3), Tensor.randn(8, 3)).shape, "shape").toEqual([6, 3]))
test("the first token only sees itself: row 0 equals its own value vector", () => {
  const __x = Tensor.randn(4, 6), __wv = Tensor.randn(6, 2)
  const r0 = head(__x, Tensor.randn(6, 2), Tensor.randn(6, 2), __wv).row(0)
  expect(r0, "head(...).row(0)").toBeCloseTo(__x.matmul(__wv).row(0), 1e-9)
})
test("future tokens don't influence the past (causality)", () => {
  const __wq = Tensor.randn(4, 2), __wk = Tensor.randn(4, 2), __wv = Tensor.randn(4, 2)
  const a = Tensor.from([[1, 0, 0, 1], [0, 1, 1, 0], [1, 1, 0, 0]])
  const b = Tensor.from([[1, 0, 0, 1], [0, 1, 1, 0], [9, -9, 9, -9]])
  expect(head(a, __wq, __wk, __wv).row(1), "row 1 with different future").toBeCloseTo(head(b, __wq, __wk, __wv).row(1), 1e-9)
})
test("gradients flow back to the weights", () => {
  const __wq = Tensor.randn(4, 2)
  head(Tensor.randn(3, 4), __wq, Tensor.randn(4, 2), Tensor.randn(4, 2)).sum().backward()
  expect(Math.max(...Array.from(__wq.grad).map(Math.abs)), "largest |grad| of wq").toBeGreaterThan(0)
})
`,
}

export const gptFull: ExerciseDef = {
  starter: `// The whole GPT, in about 60 lines of your own stdlib. Run it (takes ~20 seconds).
const text = data.stories.replace(/\\s+/g, " ")
const chars = [...new Set(text)].sort()
const ids = [...text].map(c => chars.indexOf(c))
const V = chars.length, T = 32, C = 48, H = 4, hs = C / H, L = 2

const r = (a: number, b: number, s = 0.08) => Tensor.randn(a, b, s)
const tok = r(V, C), pos = r(T, C)
const blocks = range(L).map(() => ({
  g1: Tensor.ones(1, C), b1: Tensor.zeros(1, C),
  heads: range(H).map(() => ({ q: r(C, hs), k: r(C, hs), v: r(C, hs) })),
  wo: r(C, C, 0.04), g2: Tensor.ones(1, C), b2: Tensor.zeros(1, C),
  w1: r(C, 4 * C), w2: r(4 * C, C, 0.04),
}))
const gf = Tensor.ones(1, C), bf = Tensor.zeros(1, C), out = r(C, V)
const params = [tok, pos, gf, bf, out, ...blocks.flatMap(b => [b.g1, b.b1, b.wo, b.g2, b.b2, b.w1, b.w2, ...b.heads.flatMap(h => [h.q, h.k, h.v])])]
console.log("parameters:", params.reduce((s, p) => s + p.data.length, 0))

function forward(x: number[]): Tensor {
  let h = tok.gather(x).add(pos.gather(range(x.length)))
  for (const b of blocks) {
    const n = h.layerNorm(b.g1, b.b1)
    const heads = b.heads.map(w => {
      const att = n.matmul(w.q).matmul(n.matmul(w.k).transpose()).mul(1 / Math.sqrt(hs)).maskCausal().softmax()
      return att.matmul(n.matmul(w.v))
    })
    h = h.add(Tensor.concatCols(heads).matmul(b.wo))                      // attention + residual
    h = h.add(h.layerNorm(b.g2, b.b2).matmul(b.w1).gelu().matmul(b.w2))   // MLP + residual
  }
  return h.layerNorm(gf, bf).matmul(out)
}

function sample(prompt: string, n: number): string {
  let s = [...prompt].map(c => chars.indexOf(c))
  for (let i = 0; i < n; i++) {
    const logits = forward(s.slice(-T)).row(Math.min(s.length, T) - 1)
    s.push(random.sample(vec.softmax(logits, 0.7)))
  }
  return s.map(i => chars[i]).join("")
}

const opt = new Adam(params, { lr: 3e-3 })
const chart = plot.live("loss")
for (let step = 0; step <= 400; step++) {
  let loss: Tensor | null = null
  for (let b = 0; b < 6; b++) {
    const i = random.int(0, ids.length - T - 1)
    const l = forward(ids.slice(i, i + T)).crossEntropy(ids.slice(i + 1, i + T + 1))
    loss = loss ? loss.add(l) : l
  }
  loss = loss!.mul(1 / 6)
  opt.zeroGrad(); loss.backward(); opt.step()
  chart.push(loss.item())
  if (step % 100 === 0) console.log(\`step \${step}  loss \${loss.item().toFixed(3)}  → "\${sample("the ", 40)}"\`)
}
`,
}

// ─── 9.5 Scaling ──────────────────────────────────────────────────────────────

export const powerLaw: ExerciseDef = {
  starter: `// Measurements from (made-up but realistic) training runs: model size N vs final loss L.
const N = [1e6, 3e6, 1e7, 3e7, 1e8, 3e8, 1e9]
const L = [5.12, 4.39, 3.71, 3.18, 2.69, 2.31, 1.95]

// A power law  L = a · N^(−k)  becomes a straight line on log-log axes:
//   ln L = ln a − k · ln N
// fitPowerLaw: fit that line (least squares on the logs) and return { a, k }.
// Recall: for y = m·x + c, the best m = Σ(x−x̄)(y−ȳ) / Σ(x−x̄)², and c = ȳ − m·x̄.
function fitPowerLaw(Ns: number[], Ls: number[]): { a: number; k: number } {
  return { a: 1, k: 0 }
}

const fit = fitPowerLaw(N, L)
console.log(fit)
console.log("predicted loss for a 10-billion-parameter model:", fit.a * Math.pow(1e10, -fit.k))
plot.xy(N.map(Math.log10), L.map(Math.log10), { title: "log loss vs log size — a straight line", xLabel: "log₁₀ N" })
`,
  solution: `const N = [1e6, 3e6, 1e7, 3e7, 1e8, 3e8, 1e9]
const L = [5.12, 4.39, 3.71, 3.18, 2.69, 2.31, 1.95]

function fitPowerLaw(Ns: number[], Ls: number[]): { a: number; k: number } {
  const x = Ns.map(Math.log)
  const y = Ls.map(Math.log)
  const xm = vec.mean(x), ym = vec.mean(y)
  let num = 0, den = 0
  for (let i = 0; i < x.length; i++) {
    num += (x[i] - xm) * (y[i] - ym)
    den += (x[i] - xm) ** 2
  }
  const m = num / den
  const c = ym - m * xm
  return { a: Math.exp(c), k: -m }
}

const fit = fitPowerLaw(N, L)
console.log(fit)
console.log("predicted loss for a 10-billion-parameter model:", fit.a * Math.pow(1e10, -fit.k))
plot.xy(N.map(Math.log10), L.map(Math.log10), { title: "log loss vs log size — a straight line", xLabel: "log₁₀ N" })
`,
  tests: `
test("recovers an exact power law", () => {
  const ns = [1, 10, 100, 1000]
  const f = fitPowerLaw(ns, ns.map(n => 7 * Math.pow(n, -0.5)))
  expect([f.a, f.k], "[a, k]").toBeCloseTo([7, 0.5], 1e-6)
})
test("the exponent for the measurements is about 0.14", () => expect(fitPowerLaw(N, L).k, "k").toBeCloseTo(0.14, 0.01))
`,
}
